import axios, { InternalAxiosRequestConfig } from 'axios';
import { AppDispatch } from '../store';
import {
  setCrmApiError,
  setDiaryApiError,
  setFeApiError,
  setImoApiError,
  setSessioneScaduta,
} from '../store/slices/RootSlice';
import { setTokenJWT } from './loginMSALAuthProcess';
import { getIsImpersonation } from './msal/MsalInstance';
import {
  extractTokenFromAuthHeader,
  isTokenExpiredOrExpiring,
  refreshTokenSilently,
  emitJwtRefreshNeeded,
} from './TokenRefreshGuard';
import { updateTokenJWTFactory } from './api/ClientServicesCloud';

export const updateMSALToken = (token: string) => {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  setTokenJWT(token);
};

/**
 * Verifica se l'URL è una chiamata API che richiede il token freshness check.
 * Escluse: URL MSAL (B2C), keepalive (gestito separatamente).
 */
const isApiUrlRequiringTokenCheck = (url: string | undefined): boolean => {
  if (!url) return false;
  return (
    url.startsWith('/focusapi') ||
    url.startsWith('/crmapi') ||
    url.startsWith('/imoapi') ||
    url.startsWith('/feapi') ||
    url.startsWith('/diaryapi')
  );
};

/**
 * Interceptor che verifica la freschezza del token JWT prima di ogni richiesta API.
 * Se il token scade entro 60 secondi, esegue un refresh silenzioso tramite MSAL.
 * Usa un lock per evitare refresh concorrenti.
 */
const tokenFreshnessInterceptor = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  if (!isApiUrlRequiringTokenCheck(config.url)) return config;

  const currentToken = extractTokenFromAuthHeader(
    config.headers?.Authorization as string | undefined,
  );
  if (!currentToken) return config;

  if (isTokenExpiredOrExpiring(currentToken)) {
    console.warn(
      '[tokenFreshnessInterceptor] Token scaduto o in scadenza, eseguo refresh...',
    );
    const newToken = await refreshTokenSilently();
    if (newToken) {
      // Aggiorna il token su tutti i layer
      updateMSALToken(newToken);
      updateTokenJWTFactory(newToken);
      // Aggiorna l'header di QUESTA specifica richiesta
      config.headers.Authorization = `Bearer ${newToken}`;
    }
  }

  return config;
};

const setMSALLoginAxiosDefaults = (
  token: string,
  VMPath: string,
  dispatch: AppDispatch,
) => {
  if (!token || !VMPath) return;

  updateMSALToken(token);
  const isImpersonation = getIsImpersonation();

  // Registra l'interceptor di token freshness check (verifica token pre-request)
  axios.interceptors.request.use(tokenFreshnessInterceptor, undefined);

  if (!import.meta.env.VITE_NX_PUBLIC_LOCALAPI) {
    if (import.meta.env.VITE_NX_PUBLIC_useXRouteHeader === 'false') {
      console.log('VMPath', VMPath);
      axios.defaults.baseURL = isImpersonation
        ? `${import.meta.env.VITE_NX_PUBLIC_API_URL_IMPERSONATE}`
        : `${import.meta.env.VITE_NX_PUBLIC_API_URL}`;

      //@ts-expect-error global variable
      window.VMPath = VMPath;

      axios.interceptors.request.use(
        function (config) {
          if (
            config.url &&
            (config.url.startsWith('/crmapi') ||
              config.url.startsWith('/imoapi') ||
              config.url.startsWith('/feapi') ||
              config.url.startsWith('/focusapi'))
          ) {
            config.url = `${VMPath}${config.url}`;
          }

          return config;
        },
        function (error) {
          return Promise.reject(error);
        },
        { synchronous: true },
      );
    } else {
      axios.defaults.baseURL = isImpersonation
        ? `${import.meta.env.VITE_NX_PUBLIC_API_URL_IMPERSONATE}`
        : `${import.meta.env.VITE_NX_PUBLIC_API_URL}`;
      axios.defaults.headers.common['X-Route'] = VMPath;
    }
  } else {
    axios.interceptors.request.use(
      function (config) {
        if (
          config.url &&
          !(
            config.url.startsWith('/crmapi') ||
            config.url.startsWith('/imoapi') ||
            config.url.startsWith('/feapi') ||
            config.url.startsWith('/focusapi')
          )
        ) {
          config.baseURL = import.meta.env.VITE_NX_PUBLIC_INIT_URI;
        }

        return config;
      },
      function (error) {
        return Promise.reject(error);
      },
      { synchronous: true },
    );
  }

  const gestioneSessioneScaduta = () => {
    //@ts-expect-error global variable
    if (!window.sessioneScadutaCheck) {
      //@ts-expect-error global variable
      window.sessioneScadutaCheck = true;
    }
    dispatch(setSessioneScaduta(true));
  };

  axios.defaults.withCredentials = false;

  axios.interceptors.response.use(
    function (response) {
      if (
        response.request.responseURL.indexOf('/focusapi/tabella') >= 0 &&
        Object.prototype.hasOwnProperty.call(response.data, 'success') &&
        !response.data.success
      ) {
        return Promise.reject(response.data);
      }
      return response;
    },
    function (error) {
      if (error.response && error.response.status === 401) {
        if (error.request && error.request.responseURL) {
          const isFocusApi =
            error.request.responseURL.indexOf('focusapi/') >= 0;
          const isImoApi = error.request.responseURL.indexOf('imoapi/') >= 0;
          const isCrmApi = error.request.responseURL.indexOf('crmapi/') >= 0;
          const isDiaryApi =
            error.request.responseURL.indexOf('diaryapi/') >= 0;
          const isFeApi = error.request.responseURL.indexOf('feapi/') >= 0;

          if (!isFocusApi) {
            if (isImoApi) {
              dispatch(setImoApiError(true));
            } else if (isCrmApi) {
              dispatch(setCrmApiError(true));
            } else if (isDiaryApi) {
              dispatch(setDiaryApiError(true));
            } else if (isFeApi) {
              dispatch(setFeApiError(true));
            }

            return Promise.reject(error);
          }
        }
        // Durante il cambio utente, la sessione è terminata intenzionalmente
        if (window.switchUserInProgress) {
          return Promise.reject(error);
        }

        console.error('Sessione scaduta - 401 ricevuto da FocusAPI', error);

        // Guard 401 post-init: se il 401 arriva nei primi 15s dopo l'init, il token
        // potrebbe essere stale. Tenta un refresh prima di dichiarare sessione scaduta
        // — evita il "kick post-init" su Edge con account aziendali
        if (localStorage.getItem('focus.auth.v2.earlyRetry401') !== '0') {
          //@ts-expect-error proprietà globale per tracking timestamp init
          const initTimestamp: number | undefined = window.__focusInitTimestamp;
          if (initTimestamp && Date.now() - initTimestamp < 15_000) {
            console.warn(
              '[401] Early 401 post-init, emetto jwt-refresh-needed invece di sessioneScaduta',
            );
            emitJwtRefreshNeeded();
            return Promise.reject(error);
          }
        }

        gestioneSessioneScaduta();
        return null;
      }

      // Controlla se il backend ha risposto con "jwt non valido" (status non-401)
      // In questo caso tenta un refresh del token prima di dichiarare sessione scaduta
      const responseData = error.response?.data;
      if (
        responseData &&
        typeof responseData === 'string' &&
        (responseData.toLowerCase().includes('jwt non valido') ||
          responseData.toLowerCase().includes('ent non valido'))
      ) {
        console.warn(
          '[axios response] Rilevato "jwt non valido" dal backend, emetto evento refresh',
          { status: error.response?.status, data: responseData },
        );
        emitJwtRefreshNeeded();
      }

      return Promise.reject(error);
    },
  );
};

export default setMSALLoginAxiosDefaults;
